import { Verification as VerificationType } from "@/types/verification";
import constant from "@/utils/constant";
import { i18n } from "@/utils/i18n";
import { Box, Button, CircularProgress, IconButton, TableCell, TableRow } from "@mui/material";
import React from "react";
import { useDownload, usePreview } from "./hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { FileDescriptor } from "@/components/common/modals/file-viewer/context";
import { formatImportSource } from "@/utils/helpers";

const { CURRENCIES } = constant;

interface Props {
    verification: VerificationType;
    attachmentsCache: React.MutableRefObject<Map<number, FileDescriptor>>;
}

const Verification: React.FC<Props> = ({ verification, attachmentsCache }) => {
    const {
        transaction_id,
        transaction: { date, description, amount, attachments, import_method },
        saldo,
        splits,
    } = verification as VerificationType & {
        saldo: number;
        splits: Array<{ name: string | null; amount: number }>;
        endAmount: number;
    };

    const hasAttachments = attachments && attachments.length > 0;
    const filename = hasAttachments ? attachments[0].original_filename : null;

    const { loading: downloading, onDownload } = useDownload();
    const { loading: previewing, onPreview } = usePreview(attachmentsCache);

    return (
        <TableRow key={transaction_id}>
            <TableCell>#{transaction_id}</TableCell>
            <TableCell>{new Date(date).toLocaleDateString("sv-SE")}</TableCell>
            <TableCell>{formatImportSource(import_method)}</TableCell>
            <TableCell>
                <Box fontWeight="bold">{description ?? ""}</Box>
                {splits?.map((split, idx) => (
                    <Box key={idx} sx={{ opacity: 0.8 }}>
                        {split.name}
                    </Box>
                ))}
            </TableCell>
            <TableCell align="right">
                <Box fontWeight="bold">{parseFloat(amount).toFixed(2)} kr</Box>
                {splits?.map((split, idx) => (
                    <Box key={idx} sx={{ opacity: 0.8 }}>
                        {parseFloat(String(split.amount)).toFixed(2)} kr
                    </Box>
                ))}
            </TableCell>
            <TableCell align="right">{CURRENCIES.KRONA.format(Number(saldo))}</TableCell>
            <TableCell>
                {hasAttachments ? (
                    <Box display="flex" gap="5px" alignItems="center">
                        {previewing ? (
                            <CircularProgress size={20} color="primary" sx={{ ml: 1 }} />
                        ) : (
                            <IconButton
                                size="small"
                                sx={{ cursor: "pointer" }}
                                onClick={() => onPreview(transaction_id, filename)}
                                color="primary"
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        )}

                        {downloading ? (
                            <CircularProgress size={20} color="primary" sx={{ mr: 1 }} />
                        ) : (
                            <Button onClick={() => onDownload(transaction_id, filename)}>
                                {i18n.t("download")}
                            </Button>
                        )}
                    </Box>
                ) : (
                    "-"
                )}
            </TableCell>
        </TableRow>
    );
};

export default Verification;
